import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API_BASE from '../config';

export default function CountryManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // country object being edited
  const [form, setForm] = useState({ name: '', code: '' });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/countries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load countries');
      setCountries(data.countries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', code: '' });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, code: c.code });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(''); setSuccess('');
    try {
      const url = editing
        ? `${API_BASE}/admin/countries/${editing.id}`
        : `${API_BASE}/admin/countries`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(editing ? 'Country updated.' : 'Country created.');
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete country "${name}"? This will also delete all its clients.`)) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/countries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Country deleted.');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 8 }}>← Back</button>
          <h1 style={s.title}>🌍 Country Management</h1>
            <p style={s.sub}>Manage countries for document scoping and access control</p>
          </div>
          {isSuperAdmin && (
            <button style={s.btnPrimary} onClick={openCreate}>+ Add Country</button>
          )}
        </div>

        {error   && <div style={s.alertErr}>{error}</div>}
        {success && <div style={s.alertOk}>{success}</div>}

        {showForm && (
          <div style={s.card}>
            <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>{editing ? 'Edit Country' : 'New Country'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Country Name *</label>
                  <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Bulgaria" required />
                </div>
                <div>
                  <label style={s.label}>Code (ISO 2) *</label>
                  <input style={s.input} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="BG" maxLength={4} required />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={s.btnPrimary} type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button style={s.btnGhost} type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div style={s.center}>Loading…</div>
        ) : countries.length === 0 ? (
          <div style={s.empty}>No countries yet. {isSuperAdmin && 'Click "Add Country" to create one.'}</div>
        ) : (
          <div style={s.grid3}>
            {countries.map(c => (
              <div key={c.id} style={s.countryCard}>
                <div style={s.badge}>{c.code}</div>
                <h3 style={{ margin: '8px 0 4px', fontSize: '1.05rem', color: '#1e293b' }}>{c.name}</h3>
                <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: '0.85rem' }}>
                  {c.clients?.length || 0} client{c.clients?.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {c._count?.users || 0} user{c._count?.users !== 1 ? 's' : ''}
                </p>
                {c.clients?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {c.clients.map(cl => (
                      <span key={cl.id} style={s.clientChip}>{cl.name}</span>
                    ))}
                  </div>
                )}
                {isSuperAdmin && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={s.btnSm} onClick={() => openEdit(c)}>Edit</button>
                    <button style={{ ...s.btnSm, ...s.btnDanger }} onClick={() => handleDelete(c.id, c.name)}>Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', padding: '32px 16px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: '1.6rem', color: '#1e293b', fontWeight: 700 },
  sub: { margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' },
  card: { background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  alertErr: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: 16 },
  alertOk: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '10px 16px', borderRadius: 8, marginBottom: 16 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  countryCard: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  badge: { display: 'inline-block', background: '#dbeafe', color: '#1e40af', borderRadius: 6, padding: '2px 10px', fontSize: '0.8rem', fontWeight: 700, letterSpacing: 1 },
  clientChip: { background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '2px 8px', fontSize: '0.78rem' },
  label: { display: 'block', marginBottom: 6, color: '#374151', fontWeight: 600, fontSize: '0.85rem' },
  input: { width: '100%', padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box' },
  btnPrimary: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnGhost: { background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSm: { background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 12px', fontSize: '0.8rem', cursor: 'pointer' },
  btnDanger: { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' },
  center: { textAlign: 'center', padding: 40, color: '#94a3b8' },
  empty: { background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }
};
